import { EditOutlined, UploadOutlined } from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { EmptyState } from '../../components/ui/EmptyState'
import { Skeleton } from '../../components/ui/Skeleton'
import { rfqNumberDisplay } from './domain'
import type { RfqDraftSummary } from './domain'

export interface RfqDraftPanelProps {
  readonly summary: RfqDraftSummary
  /** ADR-0082 (UID-242) — the real, server-assigned id minted eagerly on the
   * compose screen's 0→1 parts transition; `undefined` until the eager
   * `POST /rfq` resolves (see `creatingDraft`). */
  readonly draftRfqId: string | undefined
  /** UID-309 — the server-derived sequential number (ADR-0093 C1) the SAME
   * eager `POST /rfq` response carries alongside `draftRfqId`. Formatted via
   * the shared `rfqNumberDisplay` helper, which falls back to the old
   * uuid-fragment convention (`` `RFQ-${id.slice(-6).toUpperCase()}` ``, the
   * same one this panel used to hand-roll) only when the server omitted the
   * number — never a fabricated id (ADR-0088 D5-id). */
  readonly draftRfqNumber?: string | undefined
  /** True while the eager `POST /rfq` is in flight. Combined with
   * `draftRfqId === undefined`, this is the ONLY window in which the
   * identifier renders a skeleton instead of the real id (ADR-0088 D5-id) —
   * never a blank slot, never a made-up value. */
  readonly creatingDraft: boolean
  /**
   * Code review W6 (a11y, WCAG 2.4.6/4.1.2) — how to name THIS card's draft
   * in its buttons' accessible names when there is no `draftRfqId` to format
   * yet (the eager `POST /rfq` hasn't resolved, or failed). The caller passes
   * something STABLE and unique per draft — `RfqListPage` passes a fragment
   * of the slot's immutable `slotId`; a positional index would be wrong here,
   * because discarding one card would relabel all the others.
   *
   * Optional: a single-panel caller with nothing better to say may omit it
   * and get the generic wording below, which is no worse than the
   * pre-W6 state (an unqualified "Discard") and only occurs when there is
   * exactly one card, i.e. nothing to disambiguate from.
   */
  readonly draftLabel?: string | undefined
  /** "Resume" — returns to `/rfq/new`; the store's `draft` rehydrates the
   * compose screen (ADR-0049). */
  readonly onResume: () => void
  /** Fired directly on Discard click — `dc.html:3426`/`3437`'s
   * `draftBanner.onDiscard` has no confirmation step, so neither does this.
   * The parent owns the actual `discard()` + any post-discard
   * navigation/focus concerns (it knows what replaces this panel once the
   * draft is gone). */
  readonly onDiscardConfirmed: () => void
}

/**
 * The RFQ list's Draft tab panel (UID-113, ADR-0049; fidelity pass UID-202,
 * ADR-0078; solid-card refresh UID-266, ADR-0088) — a solid card matching
 * every other RFQ list item, titled by the draft's REAL RFQ identifier (not
 * a static "Draft RFQ in progress" label), with a leading amber icon chip,
 * a "Not submitted" status chip, and a single meta line (part count + up to
 * 3 part names, then "+N more" + need-by date, joined by " · ", each
 * segment conditionally present). Resume is the primary action; Discard is
 * an error-toned outline button that fires immediately on click, matching
 * `dc.html:3426`/`3437`'s `draftBanner.onDiscard` — the design has no
 * confirmation step for this action (unlike Remove part/Delete BOM/Move to
 * new RFQ, which do have their own confirm modals elsewhere).
 *
 * Copy: verbatim per the Gate 2 approved UX spec + ADR-0088 — the identifier
 * replaces the old static title, "Not submitted" status chip, meta line
 * segments "{N} part(s)" / comma-joined part names / "Need by {date}"
 * (need-by omitted entirely, no fallback copy, when unset — ADR-0078 D5,
 * unchanged).
 */
export function RfqDraftPanel({
  summary,
  draftRfqId,
  draftRfqNumber,
  creatingDraft,
  draftLabel,
  onResume,
  onDiscardConfirmed,
}: RfqDraftPanelProps) {
  const { partCount, partNames, needByDate } = summary

  // Gate 5 code review (LOW-2) — ADR-0101 made this tab unconditional, so an
  // empty bundle now reaches this panel too. Before this fix there was no
  // branch for that: `draftRfqId` is `undefined` for an empty bundle (no
  // draft has ever been created), which made `showIdSkeleton` below true
  // FOREVER — an eternal shimmer on a "0 parts" card, with live
  // Discard/Resume buttons that have nothing to discard or resume. A real
  // empty state, matching every other vertical's `EmptyState` convention,
  // replaces that permanently-loading illusion.
  if (partCount === 0) {
    return (
      /* UID-325 (F3) — `variant="dashed"`, the same variant the sibling
       * Requested/Quoted/Expired tab empty states already use (UID-294 §9).
       * This one was the only RFQ empty state still on the plain default, so
       * it rendered at the shared 16px/700 + 14px/500 while its siblings
       * rendered the design's 14.5px/700 + 13px/500 — the build was
       * inconsistent with ITSELF, which is what identified this as a
       * one-site miss rather than a token change. Reusing the existing
       * variant rather than adding a third type scale.
       *
       * The "Start a new RFQ" CTA is KEPT, but note the ticket asked for PM
       * confirmation on it, and this is an engineering judgement rather than
       * that confirmation: the CTA is wired to the panel's real `onResume`
       * entry point, and dropping it leaves a dead-end empty state on the one
       * tab whose whole purpose is starting a draft. Recorded as an open
       * question in ADR-0109's deferrals — remove it if the PM says the design
       * omits it deliberately. */
      <EmptyState
        variant="dashed"
        icon={<UploadOutlined aria-hidden="true" />}
        title="No draft in progress"
        detail="Add parts from your library, a BOM, or a part's detail page to start a new RFQ."
        action={<Button onClick={onResume}>Start a new RFQ</Button>}
      />
    )
  }

  const moreCount = partCount - partNames.length

  const metaSegments: string[] = [`${partCount} part${partCount === 1 ? '' : 's'}`]
  if (partNames.length > 0) {
    metaSegments.push(`${partNames.join(', ')}${moreCount > 0 ? `, +${moreCount} more` : ''}`)
  }
  if (needByDate) {
    metaSegments.push(`Need by ${needByDate}`)
  }

  // ADR-0088 D5-id: never fabricate an id. The skeleton covers BOTH the
  // expected in-flight window (creatingDraft && !draftRfqId) and the
  // defensive case where draftRfqId is somehow absent outside that window —
  // the panel renders a placeholder rather than crash or invent a value.
  const showIdSkeleton = draftRfqId === undefined

  /**
   * Code review W6 (a11y) — how this ONE draft is named in its two buttons'
   * accessible names, so N cards do not present N identically-named
   * "Discard"/"Resume draft" controls.
   *
   * Both labels keep their VISIBLE text as a prefix ("Discard" ⊂ "Discard
   * RFQ-2026-0001"; "Resume draft" ⊂ "Resume draft RFQ-2026-0001"), which
   * WCAG 2.5.3 Label in Name requires — speech-input users must be able to
   * say what they can read.
   */
  const draftIdentity =
    draftRfqId !== undefined
      ? rfqNumberDisplay({ id: draftRfqId, rfqNumber: draftRfqNumber ?? null })
      : (draftLabel ?? 'in progress')

  return (
    <div className="rfq-draft-card" role="note">
      <span className="rfq-draft-card-icon-chip" aria-hidden="true">
        <EditOutlined aria-hidden="true" />
      </span>
      <div className="rfq-draft-card-details">
        <div className="rfq-draft-card-heading">
          {showIdSkeleton ? (
            <span
              className="rfq-draft-card-id-skeleton-wrap"
              data-testid="rfq-draft-card-id-skeleton"
            >
              <Skeleton width={88} height={17} variant="text" />
            </span>
          ) : (
            /* The same `draftIdentity` the buttons' accessible names use, so
             * the visible title and the announced control names can never
             * name the draft differently. */
            <p className="rfq-draft-card-id">{draftIdentity}</p>
          )}
          <span className="rfq-draft-card-status-chip">Not submitted</span>
        </div>
        <p className="rfq-draft-card-meta">{metaSegments.join(' · ')}</p>
      </div>
      <div className="rfq-draft-card-actions">
        <Button
          variant="outline"
          className="rfq-draft-action-btn rfq-draft-card-discard-btn"
          aria-label={`Discard ${draftIdentity}`}
          onClick={onDiscardConfirmed}
        >
          Discard
        </Button>
        <Button
          variant="primary"
          className="rfq-draft-action-btn"
          aria-label={`Resume draft ${draftIdentity}`}
          onClick={onResume}
        >
          Resume draft
        </Button>
      </div>
    </div>
  )
}
